import React, { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%!&*])[A-Za-z\d@#$%!&*]{8,}$/;

const ChangePasswordPage = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [errors, setErrors] = useState({});

  const { changePassword } = useAuth();

  const passwordChecks = useMemo(() => {
    const value = newPassword || '';
    return {
      length: value.length >= 8,
      upper: /[A-Z]/.test(value),
      lower: /[a-z]/.test(value),
      digit: /[0-9]/.test(value),
      special: /[@#$%!&*]/.test(value),
      noSpace: !/\s/.test(value)
    };
  }, [newPassword]);

  const validate = () => {
    const newErrors = {};
    if (!currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!newPassword) newErrors.newPassword = 'New password is required';
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your new password';

    if (newPassword && !PASSWORD_REGEX.test(newPassword)) {
      newErrors.newPassword = 'Password must meet all the listed requirements.';
    }

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'New password and confirmation do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const response = await changePassword(currentPassword, newPassword, confirmPassword);
      if (response?.success) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderCheck = (passed, label) => (
    <li className={`small d-flex align-items-center mb-1 ${passed ? 'text-success' : 'text-muted'}`}>
      <span className={`me-2 rounded-circle border d-inline-flex align-items-center justify-content-center ${passed ? 'bg-success text-white border-success' : 'border-secondary'}`} style={{ width: 16, height: 16, fontSize: 10 }}>
        {passed ? '✓' : ''}
      </span>
      <span>{label}</span>
    </li>
  );

  const roleLabel = useMemo(() => {
    if (!user) return '';
    const value = parseInt(user.role);
    switch (value) {
      case 0: return 'Super Admin';
      case 1: return 'User';
      case 2: return 'Organization Admin';
      default: return 'User';
    }
  }, [user]);

  return (
    <div className="container-fluid px-0 py-3 py-md-4">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 col-xl-6">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-0 pb-0">
              <h4 className="mb-1 fw-bold">Change Password</h4>
              {user && (
                <p className="text-muted mb-1">
                  You are logged in as <strong>{user.email}</strong> ({roleLabel}).
                </p>
              )}
              <p className="text-muted small mb-0">
                For your security, choose a strong, unique password that you don&apos;t use anywhere else.
              </p>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="row g-4">
                {/* Current Password */}
                <div className="col-12">
                  <label className="form-label fw-semibold">Current Password</label>
                  <div className="input-group">
                    <input
                      type={showCurrent ? 'text' : 'password'}
                      className={`form-control ${errors.currentPassword ? 'is-invalid' : ''}`}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm px-3"
                      onClick={() => setShowCurrent(!showCurrent)}
                    >
                      {showCurrent ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {errors.currentPassword && (
                    <div className="invalid-feedback d-block mt-1">{errors.currentPassword}</div>
                  )}
                </div>

                {/* New Password */}
                <div className="col-12">
                  <label className="form-label fw-semibold">New Password</label>
                  <div className="input-group">
                    <input
                      type={showNew ? 'text' : 'password'}
                      className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm px-3"
                      onClick={() => setShowNew(!showNew)}
                    >
                      {showNew ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <div className="invalid-feedback d-block mt-1">{errors.newPassword}</div>
                  )}
                </div>

                {/* Password Rules */}
                <div className="col-12">
                  <div className="h-100 bg-light border rounded-3 p-3">
                    <label className="form-label fw-semibold mb-2">Password must contain:</label>
                    <ul className="list-unstyled mb-0">
                      {renderCheck(passwordChecks.length, 'At least 8 characters')}
                      {renderCheck(passwordChecks.upper, 'At least one uppercase letter')}
                      {renderCheck(passwordChecks.lower, 'At least one lowercase letter')}
                      {renderCheck(passwordChecks.digit, 'At least one number')}
                      {renderCheck(passwordChecks.special, 'At least one special character (@ # $ % ! & *)')}
                      {renderCheck(passwordChecks.noSpace, 'No spaces')}
                    </ul>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="col-12">
                  <label className="form-label fw-semibold">Confirm New Password</label>
                  <div className="input-group">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm px-3"
                      onClick={() => setShowConfirm(!showConfirm)}
                    >
                      {showConfirm ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <div className="invalid-feedback d-block mt-1">{errors.confirmPassword}</div>
                  )}
                </div>

                <div className="col-12 d-flex justify-content-end mt-3">
                  <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                    {loading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
